import { LoadingOutlined } from '@ant-design/icons';
import { Button, Card, Spin, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import { ACTIVATED, SUBSCRIPTION_PENDING, markActivationContinuation, readPendingOnboarding, savePendingOnboarding } from 'services/auth/onboardingStatus';
import AccountActivationStep from './components/AccountActivationStep';

const VALID_STATUSES = ['VALID', 'INVALID', 'EXPIRED', 'ALREADY_USED'];
const MOCK_TOKEN_STATUSES = {
  'mock-valid': 'VALID',
  'mock-expired': 'EXPIRED',
  'mock-invalid': 'INVALID',
  'mock-used': 'ALREADY_USED',
};
export default function AccountActivationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);
  const pendingActivation = readPendingOnboarding();
  const email = searchParams.get('email') || pendingActivation?.email || 'your work email';
  const planLabel = pendingActivation?.planTitle || pendingActivation?.planCode;
  const token = searchParams.get('token');
  const requestedStatus = searchParams.get('status')?.toUpperCase();
  const tokenStatus = VALID_STATUSES.includes(requestedStatus)
    ? requestedStatus
    : MOCK_TOKEN_STATUSES[token] || 'INVALID';
  const signIn = () => navigate(`/?redirectTo=${encodeURIComponent(`${APP_ROUTE_PREFIX}/${RouteConstant.tenantSelection}`)}`);
  const firstSignIn = () => {
    markActivationContinuation(email);
    navigate(`/?redirectTo=${encodeURIComponent('/welcome')}`);
  };
  const updateDraft = (changes) => {
    if (!pendingActivation) return;
    savePendingOnboarding({ ...pendingActivation, ...changes });
  };
  const resendActivation = () => updateDraft({ status: 'PENDING_ACTIVATION', requestedAt: new Date().toISOString() });
  const completeActivation = () => updateDraft({ status: ACTIVATED, onboardingStatus: SUBSCRIPTION_PENDING, activatedAt: new Date().toISOString() });

  useEffect(() => {
    setIsVerifying(true);
    const timerId = window.setTimeout(() => setIsVerifying(false), 900);
    return () => window.clearTimeout(timerId);
  }, [token, requestedStatus]);

  return (
    <main className="onboarding-page onboarding-activation-page">
      <div className="onboarding-shell">
        <header className="onboarding-header">
          <div><Typography.Text className="onboarding-eyebrow">WORKPLANSIQ</Typography.Text><Typography.Title level={2}>Activate your account</Typography.Title><Typography.Paragraph type="secondary">Verify your invitation and secure your administrator access.</Typography.Paragraph></div>
          <div className="onboarding-header-actions">
            <Button onClick={signIn}>Sign in</Button>
          </div>
        </header>
        <Card className="onboarding-card">
          {isVerifying ? (
            <section className="onboarding-activation" role="status" aria-live="polite">
              <Spin indicator={<LoadingOutlined spin />} size="large" />
              <Typography.Title level={2}>Verifying your activation link…</Typography.Title>
              <Typography.Paragraph type="secondary">Please wait while we check that this invitation is valid and ready to use.</Typography.Paragraph>
            </section>
          ) : (
            <AccountActivationStep email={email} planLabel={planLabel} tokenStatus={tokenStatus} onActivated={completeActivation} onFirstSignIn={firstSignIn} onResend={resendActivation} onSignIn={signIn} />
          )}
        </Card>
      </div>
    </main>
  );
}
